/**
 * The portal's URL search codec: every param is the exact text the URL carried,
 * read and written as a plain string.
 *
 * TanStack Router's default codec JSON-parses each value, so URL text that
 * happens to read as JSON comes back as something else — `?code=23456789` as a
 * number, `?code=2345E678` as `Infinity`, `?code=234567E2` as 23456700 — and
 * the router rebuilds `href` from that parsed value, which is also the href the
 * shell's sign-in bounce carries. Every route here reads its params as strings,
 * so such a value was dropped: a letter's QR link lost its code whenever the
 * code was all digits or digits around an `E`, both of which the human-code
 * alphabet can mint.
 */
export function parseSearch(searchStr: string): Record<string, string> {
  return Object.fromEntries(new URLSearchParams(searchStr));
}

/**
 * The write half: a string param is written as itself (never JSON-quoted), so
 * `parseSearch` reads back exactly what was written; any other value is written
 * as its JSON text, and an undefined or null one is left out.
 */
export function stringifySearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null) continue;
    params.set(key, typeof value === "string" ? value : JSON.stringify(value));
  }
  const encoded = params.toString();
  return encoded === "" ? "" : `?${encoded}`;
}
